// /src/reports/CashReceivedReport.js
import React, { useState, useEffect } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, addDays } from "date-fns";
import { toast } from "react-toastify";

// OneStop week start date
const ONE_STOP_WEEK1_START = new Date("2025-02-23"); // Sunday

function getOneStopWeekInfo(date) {
  const diffDays = Math.floor(
    (date - ONE_STOP_WEEK1_START) / (1000 * 60 * 60 * 24)
  );
  const weekNum = Math.floor(diffDays / 7) + 1;
  const weekStart = addDays(ONE_STOP_WEEK1_START, (weekNum - 1) * 7);
  const weekEnd = addDays(weekStart, 6);
  return { weekNum, weekStart, weekEnd };
}

export default function CashReceivedReport({ fromDate, toDate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState("daily");

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, viewType]);

  async function fetchData() {
    setLoading(true);

    const { data, error } = await supabase
      .from("zreports")
      .select("*")
      .gte("date", fromDate)
      .lte("date", toDate);

    if (error) {
      toast.error("Error fetching cash received data");
      setLoading(false);
      return;
    }

    const processed = data.map((z) => {
      const dateStr = format(new Date(z.date), "yyyy-MM-dd");

      const oneStopCash = parseFloat(z.one_stop_cash) || 0;

      const snappyCash = z.online_partners?.filter(p => p.name === "Snappy").reduce((sum, p) => sum + (p.cash || 0), 0) || 0;
      const deliverooCash = z.online_partners?.filter(p => p.name === "Deliveroo").reduce((sum, p) => sum + (p.cash || 0), 0) || 0;
      const justEatCash = z.online_partners?.filter(p => p.name === "Just Eat").reduce((sum, p) => sum + (p.cash || 0), 0) || 0;
      const otherCash = z.online_partners?.filter(p => !["Snappy", "Deliveroo", "Just Eat"].includes(p.name))
        .reduce((sum, p) => sum + (p.cash || 0), 0) || 0;

      const onlineCashTotal = snappyCash + deliverooCash + justEatCash + otherCash;
      const totalCash = oneStopCash + onlineCashTotal;

      return {
        date: dateStr,
        oneStopCash,
        snappyCash,
        deliverooCash,
        justEatCash,
        otherCash,
        onlineCashTotal,
        totalCash
      };
    });

    setRows(processed);
    setLoading(false);
  }

  function groupByWeek(data) {
    const grouped = {};
    data.forEach((row) => {
      const { weekNum, weekStart, weekEnd } = getOneStopWeekInfo(new Date(row.date));
      const key = `Week ${weekNum} (${format(weekStart, "dd MMM")} - ${format(weekEnd, "dd MMM")})`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }

  function exportCSV() {
    const headers = [
      "Date/Week",
      "OneStop Cash",
      "Snappy Cash",
      "Deliveroo Cash",
      "Just Eat Cash",
      "Other Cash",
      "Online Cash Total",
      "Total Cash Received"
    ];
    let csvRows = [headers.join(",")];

    if (viewType === "daily") {
      rows.forEach(r => {
        csvRows.push([
          r.date,
          r.oneStopCash.toFixed(2),
          r.snappyCash.toFixed(2),
          r.deliverooCash.toFixed(2),
          r.justEatCash.toFixed(2),
          r.otherCash.toFixed(2),
          r.onlineCashTotal.toFixed(2),
          r.totalCash.toFixed(2)
        ].join(","));
      });
    } else {
      const weeklyGroups = groupByWeek(rows);
      Object.keys(weeklyGroups).forEach(week => {
        weeklyGroups[week].forEach(r => {
          csvRows.push([
            week,
            r.oneStopCash.toFixed(2),
            r.snappyCash.toFixed(2),
            r.deliverooCash.toFixed(2),
            r.justEatCash.toFixed(2),
            r.otherCash.toFixed(2),
            r.onlineCashTotal.toFixed(2),
            r.totalCash.toFixed(2)
          ].join(","));
        });
      });
    }

    saveAs(new Blob([csvRows.join("\n")], { type: "text/csv" }), `cash_received_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
  }

  if (loading) return <p>Loading Cash Received Report...</p>;

  if (viewType === "daily") {
    const totals = rows.reduce((acc, r) => {
      acc.oneStopCash += r.oneStopCash;
      acc.snappyCash += r.snappyCash;
      acc.deliverooCash += r.deliverooCash;
      acc.justEatCash += r.justEatCash;
      acc.otherCash += r.otherCash;
      acc.onlineCashTotal += r.onlineCashTotal;
      acc.totalCash += r.totalCash;
      return acc;
    }, {
      oneStopCash:0, snappyCash:0, deliverooCash:0, justEatCash:0, otherCash:0, onlineCashTotal:0, totalCash:0
    });

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={e => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>Export CSV</button>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>OneStop Cash</th>
              <th>Snappy Cash</th>
              <th>Deliveroo Cash</th>
              <th>Just Eat Cash</th>
              <th>Other Cash</th>
              <th>Online Cash Total</th>
              <th>Total Cash Received</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td>{r.oneStopCash.toFixed(2)}</td>
                <td>{r.snappyCash.toFixed(2)}</td>
                <td>{r.deliverooCash.toFixed(2)}</td>
                <td>{r.justEatCash.toFixed(2)}</td>
                <td>{r.otherCash.toFixed(2)}</td>
                <td>{r.onlineCashTotal.toFixed(2)}</td>
                <td style={{ fontWeight:"bold" }}>{r.totalCash.toFixed(2)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight:"bold", background:"#f0f0f0" }}>
              <td>Total</td>
              <td>{totals.oneStopCash.toFixed(2)}</td>
              <td>{totals.snappyCash.toFixed(2)}</td>
              <td>{totals.deliverooCash.toFixed(2)}</td>
              <td>{totals.justEatCash.toFixed(2)}</td>
              <td>{totals.otherCash.toFixed(2)}</td>
              <td>{totals.onlineCashTotal.toFixed(2)}</td>
              <td>{totals.totalCash.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    const weeklyGroups = groupByWeek(rows);
    const grandTotals = rows.reduce((acc, r) => {
      acc.oneStopCash += r.oneStopCash;
      acc.snappyCash += r.snappyCash;
      acc.deliverooCash += r.deliverooCash;
      acc.justEatCash += r.justEatCash;
      acc.otherCash += r.otherCash;
      acc.onlineCashTotal += r.onlineCashTotal;
      acc.totalCash += r.totalCash;
      return acc;
    }, {
      oneStopCash:0, snappyCash:0, deliverooCash:0, justEatCash:0, otherCash:0, onlineCashTotal:0, totalCash:0
    });

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={e => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>Export CSV</button>

        {Object.keys(weeklyGroups).map(week => {
          const weekRows = weeklyGroups[week];
          const weekTotals = weekRows.reduce((acc, r) => {
            acc.oneStopCash += r.oneStopCash;
            acc.snappyCash += r.snappyCash;
            acc.deliverooCash += r.deliverooCash;
            acc.justEatCash += r.justEatCash;
            acc.otherCash += r.otherCash;
            acc.onlineCashTotal += r.onlineCashTotal;
            acc.totalCash += r.totalCash;
            return acc;
          }, {
            oneStopCash:0, snappyCash:0, deliverooCash:0, justEatCash:0, otherCash:0, onlineCashTotal:0, totalCash:0
          });

          return (
            <div key={week} style={{ marginBottom:"20px" }}>
              <h3>{week}</h3>
              <table>
                <thead>
                  <tr>
                    <th>OneStop Cash</th>
                    <th>Snappy Cash</th>
                    <th>Deliveroo Cash</th>
                    <th>Just Eat Cash</th>
                    <th>Other Cash</th>
                    <th>Online Cash Total</th>
                    <th>Total Cash Received</th>
                  </tr>
                </thead>
                <tbody>
                  {weekRows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.oneStopCash.toFixed(2)}</td>
                      <td>{r.snappyCash.toFixed(2)}</td>
                      <td>{r.deliverooCash.toFixed(2)}</td>
                      <td>{r.justEatCash.toFixed(2)}</td>
                      <td>{r.otherCash.toFixed(2)}</td>
                      <td>{r.onlineCashTotal.toFixed(2)}</td>
                      <td>{r.totalCash.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight:"bold", background:"#f0f0f0" }}>
                    <td>{weekTotals.oneStopCash.toFixed(2)}</td>
                    <td>{weekTotals.snappyCash.toFixed(2)}</td>
                    <td>{weekTotals.deliverooCash.toFixed(2)}</td>
                    <td>{weekTotals.justEatCash.toFixed(2)}</td>
                    <td>{weekTotals.otherCash.toFixed(2)}</td>
                    <td>{weekTotals.onlineCashTotal.toFixed(2)}</td>
                    <td>{weekTotals.totalCash.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}

        <h3 style={{ marginTop: "10px" }}>
          Grand Total: £{grandTotals.totalCash.toFixed(2)}
        </h3>
      </div>
    );
  }
}
